import { StatusDropdown } from '../../components/ticket/StatusDropdown';
import { PriorityIcon } from '../../components/ticket/PriorityIcon';
import { TypeIcon } from '../../components/ticket/TypeIcon';
import { Avatar } from '../../components/ui/Avatar';
import { formatPoints, versionNames } from '../../lib/format';
import { chipColours } from '../../lib/colour';
import { useTheme } from '../../theme/ThemeProvider';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import type { TicketListItem } from '../../api/types';
import { parentKeyOf } from './ticket-extras';

/**
 * One card, as in shot 6: summary, then a footer of `KEY`, type, priority, points and assignee.
 *
 * ⭐⭐ THE STATUS DROPDOWN IS ON EVERY CARD, not only in the drag. HTML5 drag-and-drop does not fire
 * for touch and is unusable with a keyboard, so a board where dragging is the ONLY way to move a
 * ticket cannot be used on Or's phone or by anyone not using a mouse. Both paths go through the same
 * `GET /tickets/:id/transitions` gate, so neither can offer a move the API would refuse.
 *
 * ⚠️ The left stripe is `status_def.colour` (shot 8) — admin-editable data, so it is a stripe rather
 * than a background: a colour picked for a chip need not be readable behind body text.
 */
export function BoardCard({
  ticket,
  detached,
  dragging,
  draggable,
  onDragStart,
  onDragEnd,
  onOpen,
  onChanged,
}: {
  ticket: TicketListItem;
  detached: boolean;
  dragging: boolean;
  draggable: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onOpen: () => void;
  onChanged: () => void;
}) {
  const vocab = useVocabulary();
  const { resolved } = useTheme();

  const status = vocab.status(ticket.status);
  const points = formatPoints(ticket.story_points);
  const stripe = chipColours(status?.colour, resolved === 'dark').background;
  // ⚠️ `undefined` means the endpoint did not send the field; `[]` means the ticket ships in nothing.
  const fixVersions = ticket.fix_versions === undefined ? null : versionNames(ticket.fix_versions);
  // ⭐ `parent_key` names the story instead of an anonymous "sub-task" badge — the API derives it in
  // the same page query, so this costs nothing and turns a label into an answer.
  const parentKey = parentKeyOf(ticket);

  return (
    <article
      data-testid="board-card"
      draggable={draggable}
      onDragStart={(event) => {
        // A payload is required by Firefox for a drag to start at all; the board reads its own state
        // and never this string.
        event.dataTransfer.setData('text/plain', ticket.key);
        event.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      style={{ borderLeftColor: stripe }}
      className={`rounded-md border border-l-4 border-[color:var(--color-line)] bg-[color:var(--color-surface-raised)] p-2 shadow-sm ${
        dragging ? 'opacity-40' : ''
      } ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left text-sm leading-snug hover:underline"
      >
        {ticket.title}
      </button>

      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        <StatusDropdown ticketId={ticket.id} statusCode={ticket.status} onChanged={onChanged} compact />
        {detached ? (
          <span
            title={`Its parent story${
              parentKey ? ` ${parentKey}` : ''
            } is outside the loaded window — shown here rather than hidden`}
            className="rounded border border-[color:var(--color-line)] px-1 text-[10px] font-semibold text-[color:var(--color-ink-muted)]"
          >
            ↳ {parentKey ?? 'sub-task'}
          </span>
        ) : null}
      </div>

      {fixVersions && fixVersions.length > 0 ? (
        <p className="mt-1 truncate text-[10px] text-[color:var(--color-ink-muted)]">
          ships in {fixVersions.join(', ')}
        </p>
      ) : null}

      <div className="mt-2 flex items-center gap-1.5">
        <TypeIcon type={vocab.type(ticket.type)} size={13} />
        <span className="font-mono text-[11px] text-[color:var(--color-ink-muted)]">{ticket.key}</span>
        <span className="flex-1" />
        <PriorityIcon priority={vocab.priority(ticket.priority)} />
        {points ? (
          <span
            title="Story points"
            className="rounded-full bg-[color:var(--color-line)] px-1.5 text-[10px] font-semibold"
          >
            {points}
          </span>
        ) : null}
        <Avatar name={ticket.assignee} size={20} />
      </div>
    </article>
  );
}
